/**
 * Label validation shared between frontend and backend.
 *
 * Three categories describe where a label comes from (provenance), NOT who can
 * set it. Users can add any label the org has configured; the category just
 * governs UI grouping and extraction routing:
 *
 *   - document_type — mutually exclusive, at most one per attachment. Drives
 *     extraction routing (the document_type label's classification_prompt is
 *     what the extractor uses) and the tab badge.
 *
 *   - workflow_tag — additive, many allowed. Signals for downstream routing
 *     or escalation (physician_review, urgent, stat, new_patient,
 *     existing_patient).
 *
 *   - procedural — pipeline sets these automatically (voicemail is detected
 *     by the audio processor). Users can still add them manually if they want,
 *     but the UI usually groups them with workflow tags.
 *
 * The server is the source of truth; the client mirrors this to pre-validate
 * the picker UI so users never see a server-side error for a state the UI
 * already let them reach.
 */

import type { FieldError } from '../types/api';

export const LABEL_CATEGORY = {
  DOCUMENT_TYPE: 'document_type',
  WORKFLOW_TAG: 'workflow_tag',
  PROCEDURAL: 'procedural',
} as const;

export type LabelCategory = (typeof LABEL_CATEGORY)[keyof typeof LABEL_CATEGORY];

/** Minimal label definition used by validation. Mirrors the DB row. */
export interface LabelDefinition {
  name: string;
  labelCategory: LabelCategory | string; // string for forward compat with unknown categories
  enabled: boolean;
}

/**
 * Validate a proposed label set against the org's configured labels.
 * Returns field errors ready to throw as a ValidationError (BE) or to feed into
 * react-hook-form setError calls (FE).
 *
 * Rules:
 *   1. Every requested label must exist.
 *   2. Every requested label must be enabled.
 *   3. At most one document_type label.
 *
 * Notes:
 *   - Empty labels array is valid (means "no classification").
 *   - Duplicates in `requested` are silently deduped by the validator.
 */
export function validateLabelChange(
  requested: readonly string[],
  available: readonly LabelDefinition[]
): FieldError[] {
  const errors: FieldError[] = [];
  const byName = new Map(available.map((l) => [l.name, l]));
  const unique = Array.from(new Set(requested));

  for (const slug of unique) {
    const def = byName.get(slug);
    if (!def) {
      errors.push({
        field: 'labels',
        message: `Unknown label: ${slug}`,
        code: 'UNKNOWN_LABEL',
      });
      continue;
    }
    if (!def.enabled) {
      errors.push({
        field: 'labels',
        message: `Label is disabled: ${slug}`,
        code: 'DISABLED_LABEL',
      });
    }
  }

  const docTypes = unique.filter(
    (slug) => byName.get(slug)?.labelCategory === LABEL_CATEGORY.DOCUMENT_TYPE
  );
  if (docTypes.length > 1) {
    errors.push({
      field: 'labels',
      message: `Only one document type is allowed: got ${docTypes.join(', ')}`,
      code: 'MULTIPLE_DOCUMENT_TYPES',
    });
  }

  return errors;
}

/**
 * Pick the primary label (= the single document_type) from a label set.
 * Returns null if none is present. Callers should treat null primary as
 * "no classification" — tab badge empty, extraction routing skipped.
 */
export function selectPrimaryLabel(
  labels: readonly string[],
  available: readonly LabelDefinition[]
): string | null {
  const byName = new Map(available.map((l) => [l.name, l]));
  for (const slug of labels) {
    if (byName.get(slug)?.labelCategory === LABEL_CATEGORY.DOCUMENT_TYPE) {
      return slug;
    }
  }
  return null;
}

/** Partition a label set into primary + workflow tags (preserves input order for tags). */
export function partitionLabels(
  labels: readonly string[],
  available: readonly LabelDefinition[]
): { primary: string | null; workflowTags: string[] } {
  const byName = new Map(available.map((l) => [l.name, l]));
  let primary: string | null = null;
  const workflowTags: string[] = [];
  for (const slug of labels) {
    const cat = byName.get(slug)?.labelCategory;
    if (cat === LABEL_CATEGORY.DOCUMENT_TYPE && primary === null) {
      primary = slug;
    } else if (cat === LABEL_CATEGORY.WORKFLOW_TAG) {
      workflowTags.push(slug);
    }
  }
  return { primary, workflowTags };
}
